const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]'
].join(', ')

export class FocusTrap {
  constructor(options = {}) {
    this.active = false
    this.element = null
    this.previousFocus = null
    this.onEscape = options.onEscape || null
    this._handleKeyDown = this._handleKeyDown.bind(this)
  }

  activate(element) {
    if (!element || typeof document === 'undefined') return

    this.element = element
    this.previousFocus = document.activeElement
    this.active = true

    element.setAttribute('role', 'dialog')
    element.setAttribute('aria-modal', 'true')

    document.addEventListener('keydown', this._handleKeyDown, { capture: true })
    element.addEventListener('keydown', this._handleKeyDown, { capture: true })

    requestAnimationFrame(() => {
      const first = this._getFirstFocusable()
      if (first) {
        first.focus()
      } else {
        element.setAttribute('tabindex', '-1')
        element.focus()
      }
    })
  }

  deactivate() {
    if (!this.active) return

    this.active = false

    // Remove both listeners
    document.removeEventListener('keydown', this._handleKeyDown, { capture: true })
    if (this.element) {
      this.element.removeEventListener('keydown', this._handleKeyDown, { capture: true })
    }

    // Restore previous focus
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus()
    }

    this.element = null
    this.previousFocus = null
  }

  _handleKeyDown(event) {
    if (!this.active || !this.element) return

    if (event.key === 'Escape') {
      event.preventDefault()
      if (this.onEscape) {
        this.onEscape()
      }
      return
    }

    // Tab key — trap focus
    if (event.key === 'Tab') {
      const focusables = this._getFocusableElements()

      if (focusables.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const current = document.activeElement

      if (event.shiftKey) {
        if (current === first || !focusables.includes(current)) {
          event.preventDefault()
          last.focus()
        }
      } else {
        if (current === last || !focusables.includes(current)) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  }

  _getFocusableElements() {
    if (!this.element) return []

    return Array.from(this.element.querySelectorAll(FOCUSABLE_SELECTORS))
      .filter(el => {
        const style = window.getComputedStyle(el)
        const isToggleInput = el.classList.contains('cookie-drawer__toggle-input')

        let isVisible = style.display !== 'none' && style.visibility !== 'hidden'

        if (!isToggleInput) {
          isVisible = isVisible && el.offsetWidth > 0 && el.offsetHeight > 0
        }

        const isEnabled = !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')

        return isVisible && isEnabled
      })
  }

  _getFirstFocusable() {
    const elements = this._getFocusableElements()
    return elements[0] || null
  }

  destroy() {
    this.deactivate()
  }
}
